import React, { createContext, ReactNode, useState } from 'react';

const DialogBoxActionType = {
  primary: 'primary',
  secondary: 'secondary',
  cancel: 'cancel',
  danger: 'danger',
} as const;

export type DialogBoxActionType = (typeof DialogBoxActionType)[keyof typeof DialogBoxActionType];

type DialogBoxAction = {
  title: string;
  type: DialogBoxActionType,
  action: () => void;
};

type DialogBoxParams = {
  title: string;
  content: ReactNode;
  actions: DialogBoxAction[];
};

type DialogBoxContextType = {
  actions: DialogBoxAction[];
  content: ReactNode;
  title: string;

  isActive: () => boolean;
  resetDialogBox: () => void;
  setDialogBox: (params: DialogBoxParams) => void;
};

const initialState = {
  actions: [] as DialogBoxAction[],
  content: <div />,
  title: '',
};

const DialogBoxContext = createContext<DialogBoxContextType>({
  ...initialState,
  isActive: () => false,
  resetDialogBox: () => {},
  setDialogBox: () => {},
});

export const DialogBoxContextProvider = ({ children }: { children: ReactNode }) => {
  const [actions, setActions] = useState<DialogBoxAction[]>([]);
  const [content, setContent] = useState<ReactNode>(<div />);
  const [title, setTitle] = useState('');

  const resetDialogBox = () => {
    setActions(initialState.actions);
    setContent(initialState.content);
    setTitle(initialState.title);
  };

  const setDialogBox = ({ title, content, actions }: DialogBoxParams) => {
    setActions(actions);
    setContent(content);
    setTitle(title);
  };

  const isActive = () => Boolean(title && content);

  return (
    <DialogBoxContext.Provider
      value={{
        actions,
        content,
        title,
        isActive,
        resetDialogBox,
        setDialogBox,
      }}
    >
      {children}
    </DialogBoxContext.Provider>
  );
};

export default DialogBoxContext;